// src/Pages/ReceptionistDashboard/CommunicationHub.js

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Divider,
  Badge,
  Tooltip,
  ListItemAvatar,
  Avatar,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Message as MessageIcon,
  Notifications as NotificationsIcon,
  Send as SendIcon,
  BroadcastOnPersonal as BroadcastIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  MarkEmailRead as MarkReadIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import axiosInstance from '../../api/axiosInstance';

const CommunicationHub = () => {
  const [messages, setMessages] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isComposeDialogOpen, setIsComposeDialogOpen] = useState(false);
  const [isAnnouncementDialogOpen, setIsAnnouncementDialogOpen] = useState(false);
  const [notification, setNotification] = useState(null);
  const [newMessage, setNewMessage] = useState({
    recipient: '',
    subject: '',
    message: '',
    priority: 'normal',
  });
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    message: '',
    target_audience: 'all',
    priority: 'normal',
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch communication data
  const fetchCommunicationData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [messagesResponse, announcementsResponse] = await Promise.all([
        axiosInstance.get('/notifications/receptionist/messages/'),
        axiosInstance.get('/notifications/receptionist/announcements/')
      ]);
      
      setMessages(messagesResponse.data.messages || []);
      setAnnouncements(announcementsResponse.data.announcements || []);
    } catch (error) {
      console.error('Error fetching communication data:', error);
      setNotification({
        message: 'Failed to fetch communication data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCommunicationData();
  }, [fetchCommunicationData]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleComposeMessage = () => {
    setNewMessage({
      recipient: '',
      subject: '',
      message: '',
      priority: 'normal',
    });
    setIsComposeDialogOpen(true);
  };

  const handleSendAnnouncement = () => {
    setNewAnnouncement({
      title: '',
      message: '',
      target_audience: 'all',
      priority: 'normal',
    });
    setIsAnnouncementDialogOpen(true);
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      // API call to mark notification as read
      // await axios.patch(`/api/v1/notifications/${notificationId}/`, { read: true });
      
      setNotifications(prevNotifications =>
        prevNotifications.map(notif =>
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      );
      
      setNotification({
        message: 'Notification marked as read',
        severity: 'success'
      });
    } catch (error) {
      setNotification({
        message: 'Failed to mark notification as read',
        severity: 'error'
      });
    }
  };

  const handleSendMessage = async () => {
    try {
      // API call to send message
      // await axios.post('/api/v1/messages/', newMessage);
      
      const sentMessage = {
        id: Date.now(),
        sender: 'Receptionist',
        recipient: newMessage.recipient,
        subject: newMessage.subject,
        message: newMessage.message,
        priority: newMessage.priority,
        read: false,
        created_at: new Date().toISOString(),
      };
      
      setMessages(prevMessages => [sentMessage, ...prevMessages]);
      setNotification({
        message: 'Message sent successfully',
        severity: 'success'
      });
      setIsComposeDialogOpen(false);
    } catch (error) {
      setNotification({
        message: 'Failed to send message',
        severity: 'error'
      });
    }
  };

  const handleSendNewAnnouncement = async () => {
    try {
      // API call to send announcement
      // await axios.post('/api/v1/announcements/', newAnnouncement);
      
      const sentAnnouncement = {
        id: Date.now(),
        title: newAnnouncement.title,
        message: newAnnouncement.message,
        target_audience: newAnnouncement.target_audience,
        priority: newAnnouncement.priority,
        created_at: new Date().toISOString(),
        created_by: 'Receptionist',
      };
      
      setAnnouncements(prevAnnouncements => [sentAnnouncement, ...prevAnnouncements]);
      setNotification({
        message: 'Announcement sent successfully',
        severity: 'success'
      });
      setIsAnnouncementDialogOpen(false);
    } catch (error) {
      setNotification({
        message: 'Failed to send announcement',
        severity: 'error'
      });
    }
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'normal': return 'primary';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const filteredMessages = messages.filter(
    (message) =>
      message.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.sender.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.recipient.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const unreadCount = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant='h4' gutterBottom>
          Communication Hub
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchCommunicationData}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<MessageIcon />}
            onClick={handleComposeMessage}
          >
            Compose Message
          </Button>
          <Button
            variant="contained"
            startIcon={<BroadcastIcon />}
            onClick={handleSendAnnouncement}
          >
            Send Announcement
          </Button>
        </Box>
      </Box>

      {/* Communication Statistics */}
      <Grid container spacing={2} sx={{ marginBottom: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant='h6' color='primary'>
                Total Messages
              </Typography>
              <Typography variant='h4'>{messages.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant='h6' color='warning.main'>
                Unread Notifications
              </Typography>
              <Typography variant='h4'>{unreadCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant='h6' color='info.main'>
                Announcements
              </Typography>
              <Typography variant='h4'>{announcements.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant='h6' color='success.main'>
                High Priority
              </Typography>
              <Typography variant='h4'>
                {messages.filter(m => m.priority === 'high').length + 
                 announcements.filter(a => a.priority === 'high').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Messages */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ padding: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant='h6'>
                Messages
              </Typography>
              <TextField
                size="small"
                placeholder="Search messages..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: <SearchIcon />,
                }}
                sx={{ width: 250 }}
              />
            </Box>
            
            {filteredMessages.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                No messages found
              </Typography>
            ) : (
              <List>
                {filteredMessages.map((message) => (
                  <ListItem
                    key={message.id}
                    sx={{
                      backgroundColor: message.read ? 'transparent' : 'action.hover',
                      '&:hover': {
                        backgroundColor: 'action.selected',
                      },
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: message.read ? 'normal' : 'bold',
                            }}
                          >
                            {message.subject}
                          </Typography>
                          <Chip
                            label={message.priority}
                            color={getPriorityColor(message.priority)}
                            size="small"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            From: {message.sender} | To: {message.recipient}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {message.message.substring(0, 100)}...
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(message.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                      }
                    />
                    <Stack direction="row" spacing={1}>
                      {!message.read && (
                        <IconButton
                          color="primary"
                          onClick={() => handleMarkAsRead(message.id)}
                          size="small"
                        >
                          <MarkReadIcon />
                        </IconButton>
                      )}
                      <IconButton color="error" size="small">
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        {/* Announcements */}
        <Grid item xs={12} md={4}>
          <Paper elevation={2} sx={{ padding: 2 }}>
            <Typography variant='h6' gutterBottom>
              Recent Announcements
            </Typography>
            {announcements.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                No announcements
              </Typography>
            ) : (
              <List>
                {announcements.slice(0, 5).map((announcement) => (
                  <ListItem key={announcement.id} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {announcement.title}
                      </Typography>
                      <Chip
                        label={announcement.priority}
                        color={getPriorityColor(announcement.priority)}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {announcement.message.substring(0, 100)}...
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      By {announcement.created_by} • {new Date(announcement.created_at).toLocaleDateString()}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Compose Message Dialog */}
      <Dialog open={isComposeDialogOpen} onClose={() => setIsComposeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Compose Message</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Recipient"
                value={newMessage.recipient}
                onChange={(e) => setNewMessage({ ...newMessage, recipient: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                value={newMessage.subject}
                onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message"
                value={newMessage.message}
                onChange={(e) => setNewMessage({ ...newMessage, message: e.target.value })}
                multiline
                rows={4}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={newMessage.priority}
                  onChange={(e) => setNewMessage({ ...newMessage, priority: e.target.value })}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsComposeDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSendMessage}
            variant="contained"
            startIcon={<SendIcon />}
            disabled={!newMessage.recipient || !newMessage.subject || !newMessage.message}
          >
            Send Message
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Announcement Dialog */}
      <Dialog open={isAnnouncementDialogOpen} onClose={() => setIsAnnouncementDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Announcement</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={newAnnouncement.title}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message"
                value={newAnnouncement.message}
                onChange={(e) => setNewAnnouncement({ ...newAnnouncement, message: e.target.value })}
                multiline
                rows={4}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Target Audience</InputLabel>
                <Select
                  value={newAnnouncement.target_audience}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, target_audience: e.target.value })}
                  label="Target Audience"
                >
                  <MenuItem value="all">All Staff</MenuItem>
                  <MenuItem value="receptionist">Receptionists</MenuItem>
                  <MenuItem value="doctors">Doctors</MenuItem>
                  <MenuItem value="patients">Patients</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={newAnnouncement.priority}
                  onChange={(e) => setNewAnnouncement({ ...newAnnouncement, priority: e.target.value })}
                  label="Priority"
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAnnouncementDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSendNewAnnouncement}
            variant="contained"
            startIcon={<BroadcastIcon />}
            disabled={!newAnnouncement.title || !newAnnouncement.message}
          >
            Send Announcement
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={Boolean(notification)}
        autoHideDuration={4000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification?.severity}
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommunicationHub;
